<script lang="ts" setup>
import type {
  Configuration,
  SurfaceOptionsGroup,
  SurfaceTypesMapValue,
  SurfaceMaps,
} from '~/types/configurator'

const activePanel = defineModel()

const props = defineProps<{
  configuration: Configuration | null
  currentValues: Configuration['values']
  surfaceCategoryMismatch: Map<string, Map<number, boolean>>
  globalSurfaceChanges: Map<string, Map<number, boolean>>
  surfaceOptionsGroups: Map<string, SurfaceOptionsGroup>
  surfaceTypes: Map<number, SurfaceTypesMapValue>
  surfaceMaps: SurfaceMaps
  activeSurfaceOption: [string, number] | undefined
  chosenParts: Set<[number, number | null | undefined]>
}>()

const emit = defineEmits<{
  setChosenParts: [
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    partCategoryNid?: number,
    accessoryNid?: number | null,
  ]
  updateSurfaces: [
    option: Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections'][number]['surfaces'][number],
  ]
  setGlobalSurfaceChanges: [
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    globalSurfaceChange: boolean,
    value?: Configuration['values'][number],
  ]
}>()

const globalSurfaceChange = computed(() => {
  if (props.activeSurfaceOption?.length !== 2) {
    return undefined
  }

  return props.globalSurfaceChanges
    .get(props.activeSurfaceOption?.[0])
    ?.get(props.activeSurfaceOption?.[1])
})

const surfaceCategoryMismatchLocal = computed(() => {
  if (props.activeSurfaceOption?.length !== 2) {
    return undefined
  }

  return props.surfaceCategoryMismatch
    .get(props.activeSurfaceOption?.[0])
    ?.get(props.activeSurfaceOption?.[1])
})

const surfaceOptionGroup = computed(() => {
  if (props.activeSurfaceOption?.length !== 2) {
    return undefined
  }

  return props.surfaceOptionsGroups
    .get(props.activeSurfaceOption[0])
    ?.surfaceOptionGroups?.get(props.activeSurfaceOption[1])
})
</script>

<template>
  <div>
    <div
      v-if="
        globalSurfaceChange &&
        (surfaceOptionGroup?.surfaceOptions?.length || 0) > 1
      "
      class="grid h-16 w-full grid-cols-2 items-center px-5 py-2 hover:bg-neutral-100 2xl:h-20"
      :class="{
        '!max-h-16': activePanel,
        '!bg-red-50': surfaceCategoryMismatchLocal,
        'hover:!bg-red-100':
          surfaceCategoryMismatchLocal && globalSurfaceChange,
      }"
    >
      <div>
        <p class="font-pragmatica-ext font-bold 2xl:text-lg">
          {{ surfaceOptionGroup?.surfaceType?.title }}
        </p>
        <button
          v-if="globalSurfaceChange"
          class="flex items-center text-left text-xs underline"
          @click.stop="
            () => {
              if (activeSurfaceOption?.length === 2) {
                emit(
                  'setGlobalSurfaceChanges',
                  activeSurfaceOption[0],
                  activeSurfaceOption[1],
                  false
                )
                emit(
                  'setChosenParts',
                  activeSurfaceOption[0],
                  activeSurfaceOption[1],
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.partCategoryNid,
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.accessoryNid
                )
              }
            }
          "
        >
          <span>{{ $t('configurator.applyMultipleSurface') }}</span>
          <Icon name="fa6-solid:arrow-down" class="ml-2" />
        </button>
      </div>
      <div class="justify-self-end">
        <div v-show="globalSurfaceChange" class="flex items-center gap-4">
          <p class="text-right">
            <span>
              {{
                configuratorUtils.findValue(
                  props.currentValues,
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.partCategoryNid ||
                    0,
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.accessoryNid
                )?.surfaceTitle
              }}
            </span>
            <br />
            <span
              v-if="
                configuratorUtils.findValue(
                  props.currentValues,
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.partCategoryNid ||
                    0,
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.accessoryNid
                )?.surfaceNid
              "
              class="text-sm font-bold"
            >
              {{ $t('configurator.category') }}
              {{
                configuratorUtils.findSurfaceByNid(
                  props.surfaceTypes,
                  props.surfaceMaps,
                  configuratorUtils.findValue(
                    props.currentValues,
                    surfaceOptionGroup?.surfaceOptions?.at(0)
                      ?.partCategoryNid || 0,
                    surfaceOptionGroup?.surfaceOptions?.at(0)?.accessoryNid
                  )?.surfaceNid
                )?.category?.title
              }}
            </span>
          </p>
          <NuxtImg
            v-if="
              configuratorUtils.findValue(
                props.currentValues,
                surfaceOptionGroup?.surfaceOptions?.at(0)?.partCategoryNid || 0,
                surfaceOptionGroup?.surfaceOptions?.at(0)?.accessoryNid
              )?.surfaceImageSrc
            "
            :src="
              withHost(
                configuratorUtils.findValue(
                  props.currentValues,
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.partCategoryNid ||
                    0,
                  surfaceOptionGroup?.surfaceOptions?.at(0)?.accessoryNid
                )?.surfaceImageSrc + IMG_SIZE_100_100_FIT
              )
            "
            class="aspect-square h-12 rounded-full 2xl:h-16"
            alt=""
          />
          <div
            v-else
            class="font-pragmatica-ext grid h-12 w-12 place-content-center rounded-full bg-black text-xs font-bold text-white 2xl:h-16 2xl:w-16 2xl:text-sm"
          >
            <span>
              {{ props.configuration?.surfacesOptions[0]?.title }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-for="(option, index) in surfaceOptionGroup?.surfaceOptions"
      v-show="
        !globalSurfaceChange || surfaceOptionGroup?.surfaceOptions?.length === 1
      "
      :key="index"
      class="grid min-h-16 w-full cursor-pointer grid-cols-2 items-center bg-white px-5 py-2 hover:!bg-neutral-100 2xl:min-h-20"
      :class="{
        '!bg-neutral-200': [...chosenParts].some(
          (part) =>
            part[0] === option.partCategoryNid &&
            part[1] === option.accessoryNid
        ),
        '!bg-red-50 hover:!bg-red-100':
          surfaceCategoryMismatchLocal &&
          ![...chosenParts].some(
            (part) =>
              part[0] === option.partCategoryNid &&
              part[1] === option.accessoryNid
          ),
        '!bg-red-200 hover:!bg-red-100':
          surfaceCategoryMismatchLocal &&
          [...chosenParts].some(
            (part) =>
              part[0] === option.partCategoryNid &&
              part[1] === option.accessoryNid
          ),
        '!cursor-auto': surfaceOptionGroup?.surfaceOptions?.length === 1,
        '!min-h-[4.125rem] 2xl:!min-h-[5.125rem]':
          surfaceOptionGroup?.surfaceOptions?.length !== 1,
      }"
      @click.stop="
        () => {
          if (activeSurfaceOption?.length === 2) {
            emit(
              'setChosenParts',
              activeSurfaceOption[0],
              activeSurfaceOption[1],
              option.partCategoryNid,
              option.accessoryNid
            )
          }
          activePanel = SURFACES_OPTIONS_PANEL
        }
      "
    >
      <div>
        <p class="font-pragmatica-ext text-lg font-bold">
          {{ option.assignFabricTitle }}
        </p>
        <button
          v-if="
            !globalSurfaceChange &&
            surfaceOptionGroup?.surfaceOptions?.length !== 1
          "
          class="flex items-center text-left text-xs underline"
          @click.stop="
            () => {
              if (activeSurfaceOption?.length === 2) {
                emit(
                  'setGlobalSurfaceChanges',
                  activeSurfaceOption[0],
                  activeSurfaceOption[1],
                  true,
                  configuratorUtils.findValue(
                    props.currentValues,
                    option.partCategoryNid,
                    option.accessoryNid
                  )
                )
                emit(
                  'setChosenParts',
                  activeSurfaceOption[0],
                  activeSurfaceOption[1]
                )
              }
            }
          "
        >
          {{ $t('configurator.applySurfaceGlobally') }}
        </button>
      </div>
      <div class="flex items-center justify-center gap-4 justify-self-end">
        <p class="text-right">
          <span>
            {{
              configuratorUtils.findValue(
                props.currentValues,
                option.partCategoryNid,
                option.accessoryNid
              )?.surfaceTitle
            }}
          </span>
          <br />
          <span
            v-if="
              configuratorUtils.findValue(
                props.currentValues,
                option.partCategoryNid,
                option.accessoryNid
              )?.surfaceNid
            "
            class="text-sm font-bold"
          >
            {{ $t('configurator.category') }}
            {{
              configuratorUtils.findSurfaceByNid(
                props.surfaceTypes,
                props.surfaceMaps,
                configuratorUtils.findValue(
                  props.currentValues,
                  option.partCategoryNid,
                  option.accessoryNid
                )?.surfaceNid
              )?.category?.title
            }}
          </span>
        </p>
        <NuxtImg
          v-if="
            configuratorUtils.findValue(
              props.currentValues,
              option.partCategoryNid,
              option.accessoryNid
            )?.surfaceImageSrc
          "
          :src="
            withHost(
              configuratorUtils.findValue(
                props.currentValues,
                option.partCategoryNid,
                option.accessoryNid
              )?.surfaceImageSrc + IMG_SIZE_100_100_FIT
            )
          "
          class="aspect-square h-12 rounded-full 2xl:h-16"
          alt=""
        />
        <div
          v-else
          class="font-pragmatica-ext grid aspect-square h-12 place-content-center rounded-full bg-black text-xs font-bold text-white 2xl:h-14 2xl:text-sm"
        >
          <span>
            {{ option.title }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="surfaceCategoryMismatchLocal"
      class="flex w-full flex-row items-start justify-end gap-4 bg-red-50 py-2 pl-2 pr-5 2xl:pl-5"
    >
      <p class="self-center text-right text-xs text-red-800">
        {{ $t('configurator.categoryMismatch') }}
      </p>
      <span
        class="flex aspect-square min-w-10 items-center justify-center rounded-full bg-red-500 text-white"
      >
        <Icon class="size-5 pb-0.5" name="fa6-solid:triangle-exclamation" />
      </span>
    </div>
  </div>
</template>
